import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLiveClass } from '../contexts/LiveClassContext';
import {
    ArrowLeft, CheckCircle, Lock, BookOpen, ChevronRight,
    Loader2, Trophy, AlertCircle, Play, ExternalLink, X, Menu, Printer, ShieldCheck, CreditCard,
    UploadCloud, FileText, MessageSquare, Video
} from 'lucide-react';
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import { getImageUrl } from '../utils/imageUtils';
import { CardThumbnail } from '../utils/cardUtils';

const API = process.env.REACT_APP_API_BASE_URL;

// Helper to extract YouTube ID or search query
const getYouTubeInfo = (url) => {
    if (!url) return null;
    try {
        const cleanUrl = url.trim();
        // Regex for standard and shortened YT links
        const videoIdMatch = cleanUrl.match(/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
        if (videoIdMatch) {
            return { id: videoIdMatch[1], type: 'video' };
        }

        // Search query fallback
        const urlObj = new URL(cleanUrl);
        if (urlObj.pathname.includes('results') && urlObj.searchParams.has('search_query')) {
            return { query: urlObj.searchParams.get('search_query'), type: 'search' };
        }
        return null;
    } catch (e) { return null; }
};

// ─── Video Component ────────────────────────────────────────────────────────
const VideoCard = ({ title, url, onClick }) => {
    const info = getYouTubeInfo(url);
    const thumbUrl = info?.type === 'video'
        ? `https://img.youtube.com/vi/${info.id}/mqdefault.jpg`
        : `https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&q=80`; // Placeholder for search

    return (
        <button
            onClick={() => onClick(url)}
            className="w-full my-6 group relative overflow-hidden rounded-2xl border-2 border-gray-100 bg-white transition-all hover:border-indigo-300 hover:shadow-xl text-left flex flex-col sm:flex-row shadow-sm"
        >
            <div className="relative w-full sm:w-48 h-32 flex-shrink-0 overflow-hidden bg-gray-900">
                <img src={thumbUrl} alt={title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-white/50 group-hover:scale-110 transition-transform">
                        <Play className="w-6 h-6 text-white fill-white" />
                    </div>
                </div>
                {info?.type === 'search' && (
                    <div className="absolute bottom-2 right-2 bg-indigo-600 text-[8px] font-black text-white px-1.5 py-0.5 rounded tracking-widest uppercase">Search</div>
                )}
            </div>
            <div className="p-4 flex flex-col justify-center flex-1">
                <h4 className="text-xs font-black text-indigo-500 uppercase tracking-widest mb-1.5 flex items-center gap-2">
                    <Video className="w-3 h-3" />
                    Recommended Video
                </h4>
                <p className="font-black text-gray-900 text-base leading-tight group-hover:text-indigo-600 transition-colors">{title}</p>
                <div className="mt-2 flex items-center gap-2 text-xs text-gray-400 font-medium italic">
                    Click to watch lesson walkthrough
                    <ChevronRight className="w-3 h-3" />
                </div>
            </div>
        </button>
    );
};

// ─── Advanced Markdown & Scientific Renderer ────────────────────────────────
const MarkdownRenderer = ({ content, onVideoClick }) => {
    if (!content) return null;

    // Phase 1: Split into Blocks (Code, Math, Text)
    // Regex identifies: 
    // 1. ```svg...``` or `svg...` (Flexible backticks and whitespace)
    // 2. $$...$$ (Block Math)
    // 3. Regular text
    const blockRegex = /((?:```|`)svg\s*([\s\S]*?)(?:```|`?)|\$\$([\s\S]*?)\$\$|([\s\S]+?)(?=(?:```|`)svg|\$\$|$))/g;
    const blocks = [];
    let match;

    while ((match = blockRegex.exec(content)) !== null) {
        if (match[2]) {
            // SVG Block (cleaned of potential trailing marker)
            let svgContent = match[2].trim();
            blocks.push({ type: 'svg', content: svgContent });
        } else if (match[3]) {
            // Block Math
            blocks.push({ type: 'math', content: match[3] });
        } else if (match[4] && match[4].trim()) {
            // Check if the "text" block actually contains a raw <svg> tag that escaped the block parser
            const rawSvgMatch = match[4].match(/<svg[\s\S]*?<\/svg>/);
            if (rawSvgMatch) {
                const before = match[4].slice(0, rawSvgMatch.index);
                const svg = rawSvgMatch[0];
                const after = match[4].slice(rawSvgMatch.index + svg.length);
                if (before.trim()) blocks.push({ type: 'text', content: before });
                blocks.push({ type: 'svg', content: svg });
                if (after.trim()) blocks.push({ type: 'text', content: after });
            } else {
                blocks.push({ type: 'text', content: match[4] });
            }
        }
    }

    const renderInline = (text) => {
        const parts = [];
        let key = 0;

        // Pattern for Bold, Italic, Links, Code, AND Inline Math $...$
        // Added Math: \$([^\$]+)\$
        const pattern = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`|\[(.+?)\]\((https?:\/\/[^\)]+)\)|\$([^\$]+)\$)/g;
        let lastIndex = 0;
        let match;

        while ((match = pattern.exec(text)) !== null) {
            if (match.index > lastIndex) {
                parts.push(<span key={key++}>{text.slice(lastIndex, match.index)}</span>);
            }
            if (match[2]) parts.push(<strong key={key++} className="font-bold text-gray-900">{match[2]}</strong>);
            else if (match[3]) parts.push(<em key={key++} className="italic text-gray-800">{match[3]}</em>);
            else if (match[4]) parts.push(<code key={key++} className="bg-gray-100 text-indigo-700 text-xs px-1 py-0.5 rounded font-mono border border-gray-200">{match[4]}</code>);
            else if (match[5]) parts.push(
                <a key={key++} href={match[6]} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-indigo-600 hover:underline font-bold decoration-2">
                    {match[5]} <ExternalLink className="w-3 h-3" />
                </a>
            );
            else if (match[7]) parts.push(<InlineMath key={key++} math={match[7]} />);

            lastIndex = match.index + match[0].length;
        }
        if (lastIndex < text.length) parts.push(<span key={key++}>{text.slice(lastIndex)}</span>);
        return parts.length > 0 ? parts : text;
    };

    const renderTextLine = (line, i) => {
        const trimmedLine = line.trim();
        if (!trimmedLine && line.length === 0) return <div key={i} className="h-4" />;

        // Detect Video Link: [Video: Title](URL)
        const videoMatch = line.match(/^\[Video:\s*(.+?)\]\((https?:\/\/.+?)\)$/);
        if (videoMatch) {
            return <VideoCard key={i} title={videoMatch[1]} url={videoMatch[2]} onClick={onVideoClick} />;
        }

        // Headings
        if (line.startsWith('### ')) return <h3 key={i} className="text-lg font-black text-gray-900 mt-8 mb-3 flex items-center gap-2">
            <div className="w-1.5 h-6 bg-indigo-500 rounded-full" />
            {line.slice(4)}
        </h3>;
        if (line.startsWith('## ')) return <h2 key={i} className="text-2xl font-black text-indigo-950 mt-12 mb-5 border-b-4 border-indigo-100 pb-2 flex items-center gap-3">
            <div className="w-3 h-8 bg-indigo-600 rounded-lg shadow-sm" />
            {line.slice(3)}
        </h2>;
        if (line.startsWith('# ')) return <h1 key={i} className="text-3xl font-black text-indigo-900 mt-6 mb-6">{line.slice(2)}</h1>;

        // Bullet
        if (line.startsWith('- ') || line.startsWith('* ')) {
            return <li key={i} className="ml-6 text-base text-gray-700 mb-2 list-disc pl-2 marker:text-indigo-500">{renderInline(line.slice(2))}</li>;
        }
        // Numbered
        if (/^\d+\. /.test(line)) {
            return <li key={i} className="ml-6 text-base text-gray-700 mb-2 list-decimal pl-2 marker:text-indigo-600 marker:font-black">{renderInline(line.replace(/^\d+\. /, ''))}</li>;
        }
        // Horizontal rule
        if (line === '---') return <hr key={i} className="border-gray-200 my-10 border-t-2" />;

        // Regular paragraph
        return <p key={i} className="text-base text-gray-700 mb-5 leading-relaxed antialiased font-medium">{renderInline(line)}</p>;
    };

    return (
        <div className="science-notes-content space-y-6">
            {blocks.map((block, bi) => {
                if (block.type === 'svg') {
                    return (
                        <div key={bi} className="my-8 flex justify-center bg-gray-50 rounded-2xl p-6 border-2 border-gray-100 shadow-inner group transition-all hover:bg-white hover:border-indigo-100">
                            <div
                                className="max-w-full overflow-x-auto transform transition-transform group-hover:scale-[1.02]"
                                dangerouslySetInnerHTML={{ __html: block.content }}
                            />
                        </div>
                    );
                } else if (block.type === 'math') {
                    return (
                        <div key={bi} className="my-8 py-4 bg-indigo-50/30 rounded-xl flex justify-center border-y border-indigo-100/50">
                            <BlockMath math={block.content} />
                        </div>
                    );
                } else {
                    return (
                        <div key={bi} className="text-block">
                            {block.content.split('\n').map((line, li) => renderTextLine(line, `${bi}-${li}`))}
                        </div>
                    );
                }
            })}
        </div>
    );
};

// ─── Video Modal Component ──────────────────────────────────────────────────
const VideoModal = ({ url, onClose }) => {
    if (!url) return null;
    const info = getYouTubeInfo(url);
    const embedUrl = info?.type === 'video'
        ? `https://www.youtube.com/embed/${info.id}?autoplay=1`
        : `https://www.youtube.com/results?search_query=${info.query}`; // Fallback for search

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="relative w-full max-w-5xl aspect-video bg-black rounded-3xl overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.5)] border border-white/10">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white transition-all hover:scale-110"
                >
                    <X className="w-6 h-6" />
                </button>
                {info?.type === 'video' ? (
                    <iframe
                        src={embedUrl}
                        className="w-full h-full"
                        allow="autoplay; encrypted-media"
                        allowFullScreen
                        title="Lesson Video"
                    />
                ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-center p-8 bg-gradient-to-br from-indigo-900 to-black">
                        <Video className="w-16 h-16 text-indigo-400 mb-6 animate-pulse" />
                        <h2 className="text-2xl font-black text-white mb-2">Expanding your knowledge...</h2>
                        <p className="text-indigo-200 mb-8 max-w-md">We're opening YouTube to show you the best curated result for this query.</p>
                        <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-8 py-4 bg-indigo-600 text-white rounded-2xl font-black text-lg hover:bg-indigo-700 transition-all flex items-center gap-3"
                        >
                            Open Results <ExternalLink className="w-5 h-5" />
                        </a>
                    </div>
                )}
            </div>
            <div className="absolute inset-0 -z-10" onClick={onClose} />
        </div>
    );
};

// ─── Final Unit Quiz Component ───────────────────────────────────────────────
const FinalQuiz = ({ unitId, onPass }) => {
    const [quiz, setQuiz] = useState(null);
    const [loading, setLoading] = useState(true);
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [showDetailedResults, setShowDetailedResults] = useState(false);

    useEffect(() => {
        const fetchQuiz = async () => {
            try {
                const res = await fetch(`${API}/self-paced/unit/${unitId}/final-quiz`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });
                if (res.ok) setQuiz(await res.json());
                else setQuiz(null);
            } catch { setQuiz(null); }
            finally { setLoading(false); }
        };
        fetchQuiz();
    }, [unitId]);

    const handleSubmit = async () => {
        const answersArr = Object.entries(answers).map(([questionId, answer]) => ({
            questionId: parseInt(questionId), answer
        }));
        if (answersArr.length < (quiz?.questions?.length || 0)) {
            alert('Please answer all questions before submitting.'); return;
        }
        setSubmitting(true);
        try {
            const res = await fetch(`${API}/self-paced/quiz/${quiz.id}/submit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
                body: JSON.stringify({ answers: answersArr })
            });
            const data = await res.json();
            setResult(data);
            if (data.passed) onPass(data);
        } catch (e) {
            alert('Submission failed. Please try again.');
        } finally { setSubmitting(false); }
    };

    if (loading) return (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
            <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
            <p className="text-gray-600 font-medium">Loading your final assessment...</p>
        </div>
    );

    if (!quiz) return (
        <div className="text-center py-16">
            <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-3" />
            <p className="text-gray-600">The final quiz is being prepared. Please wait a moment and refresh.</p>
        </div>
    );

    if (result) {
        const correctCount = result.questionResults?.filter(r => r.correct).length || 0;
        const totalCount = result.questionResults?.length || 0;

        return (
            <div className="assessment-results-container">
                <style>{`
                    @media print {
                        .no-print, button, .roadmap-sidebar, .top-nav-bar, aside, nav { display: none !important; }
                        .assessment-results-container { margin: 0 !important; padding: 20px !important; width: 100% !important; }
                        body { background: white !important; }
                    }
                `}</style>

                {/* ── Summary card ── */}
                <div className={`rounded-2xl border-2 p-8 text-center mb-6 ${result.passed ? 'bg-gradient-to-b from-green-50 to-white border-green-200' : 'bg-gradient-to-b from-red-50 to-white border-red-200'}`}>
                    {result.passed ? (
                        <Trophy className="w-20 h-20 text-yellow-400 mx-auto mb-3 drop-shadow-md" />
                    ) : (
                        <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-3" />
                    )}
                    <h2 className={`text-3xl font-black mb-1 ${result.passed ? 'text-green-700' : 'text-red-700'}`}>
                        {result.passed ? '🎉 You Passed!' : 'Not Quite...'}
                    </h2>
                    {!result.passed && (
                        <p className="text-sm text-gray-500 mb-4">Score needed: <strong>{result.passingScore}%</strong> — Review your notes and try again.</p>
                    )}

                    {/* Score cards */}
                    <div className="grid grid-cols-3 gap-4 mt-6 max-w-lg mx-auto">
                        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                            <p className="text-3xl font-black text-indigo-600">{result.score}%</p>
                            <p className="text-xs text-gray-500 mt-1 font-medium uppercase tracking-wide">Score</p>
                        </div>
                        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                            <p className="text-3xl font-black text-gray-800">{result.earnedPoints}<span className="text-base font-semibold text-gray-400">/{result.totalPoints}</span></p>
                            <p className="text-xs text-gray-500 mt-1 font-medium uppercase tracking-wide">Points</p>
                        </div>
                        <div className={`rounded-xl border shadow-sm p-4 ${result.passed ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100'}`}>
                            <p className={`text-3xl font-black ${result.passed ? 'text-green-600' : 'text-red-500'}`}>{correctCount}<span className="text-base font-semibold text-gray-400">/{totalCount}</span></p>
                            <p className="text-xs text-gray-500 mt-1 font-medium uppercase tracking-wide">Correct</p>
                        </div>
                    </div>

                    {result.passed && (
                        <p className="mt-5 text-indigo-700 font-medium text-sm animate-pulse">
                            {result.nextUnit ? `🔓 Unlocking next unit: ${result.nextUnit.name}...` : '🏆 Course complete! Well done.'}
                        </p>
                    )}
                </div>

                {/* ── Action buttons ── */}
                <div className="flex flex-wrap gap-3 justify-center no-print mb-8">
                    {result.questionResults?.length > 0 && (
                        <button
                            onClick={() => setShowDetailedResults(prev => !prev)}
                            className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors flex items-center gap-2"
                        >
                            {showDetailedResults ? '▲ Hide Results' : '📋 View Results'}
                        </button>
                    )}
                    {!result.passed && (
                        <button onClick={() => setResult(null)} className="px-6 py-3 bg-white border-2 border-indigo-300 text-indigo-700 rounded-xl font-bold hover:bg-indigo-50 transition-colors">
                            🔄 Retry Quiz
                        </button>
                    )}
                    <button onClick={() => window.print()} className="px-6 py-3 bg-gray-800 text-white rounded-xl font-bold hover:bg-gray-900 transition-colors flex items-center gap-2">
                        <Printer className="w-4 h-4" /> Print
                    </button>
                </div>

                {/* ── Detailed results (expandable) ── */}
                {showDetailedResults && result.questionResults && (
                    <div className="space-y-4 text-left">
                        <h3 className="text-lg font-black text-gray-800 mb-3 flex items-center gap-2">
                            📝 Detailed Question Review
                            <span className="text-sm font-normal text-gray-400">({correctCount}/{totalCount} correct)</span>
                        </h3>
                        {result.questionResults.map((r, i) => (
                            <div key={i} className={`rounded-xl border p-5 ${r.correct ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                                <div className="flex items-start gap-3">
                                    <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-black ${r.correct ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                                        {r.correct ? '✓' : '✗'}
                                    </span>
                                    <div className="flex-1">
                                        <p className={`text-sm font-bold mb-2 ${r.correct ? 'text-green-800' : 'text-red-800'}`}>
                                            Question {i + 1}: {r.correct ? 'Correct' : 'Incorrect'}
                                        </p>
                                        {!r.correct && (
                                            <div className="bg-white rounded-lg px-4 py-2 text-sm mb-2 border border-gray-100">
                                                <span className="text-gray-500">Correct answer: </span>
                                                <strong className="text-gray-800">{r.correctAnswer}</strong>
                                            </div>
                                        )}
                                        {r.explanation && (
                                            <p className="text-xs text-gray-500 italic mt-1 leading-relaxed">
                                                💡 {r.explanation}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div>
            <div className="mb-6">
                <h2 className="text-xl font-black text-gray-900">{quiz.title ? quiz.title.replace('AI Quiz:', 'Smart Quiz:') : ''}</h2>
                <p className="text-sm text-gray-500 mt-1">{quiz.description ? quiz.description.replace('AI Quiz:', 'Smart Quiz:') : ''} — Pass mark: {quiz.passingScore}%</p>
            </div>
            <div className="space-y-6">
                {quiz.questions?.sort((a, b) => a.order - b.order).map((q) => (
                    <div key={q.id} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                        <p className="font-semibold text-gray-900 mb-3">{q.order + 1}. {q.text}</p>
                        {q.type === 'multiple_choice' && q.options && (
                            <div className="space-y-2">
                                {q.options.map((opt, i) => (
                                    <label key={i} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${answers[q.id] === opt ? 'bg-indigo-50 border-indigo-400' : 'border-gray-100 hover:border-gray-300'}`}>
                                        <input type="radio" name={`q-${q.id}`} value={opt} checked={answers[q.id] === opt}
                                            onChange={() => setAnswers(prev => ({ ...prev, [q.id]: opt }))}
                                            className="text-indigo-600" />
                                        <span className="text-sm text-gray-800">{opt}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                        {q.type === 'true_false' && (
                            <div className="flex gap-3">
                                {['True', 'False'].map(opt => (
                                    <label key={opt} className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg cursor-pointer border transition-colors ${answers[q.id] === opt ? 'bg-indigo-50 border-indigo-400' : 'border-gray-100 hover:border-gray-300'}`}>
                                        <input type="radio" name={`q-${q.id}`} value={opt} checked={answers[q.id] === opt}
                                            onChange={() => setAnswers(prev => ({ ...prev, [q.id]: opt }))} />
                                        <span className="text-sm font-medium">{opt}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                        {q.type === 'short_answer' && (
                            <textarea rows={2} placeholder="Type your answer..."
                                className="w-full mt-1 p-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 outline-none resize-none"
                                value={answers[q.id] || ''}
                                onChange={e => setAnswers(prev => ({ ...prev, [q.id]: e.target.value }))} />
                        )}
                    </div>
                ))}
            </div>
            <button onClick={handleSubmit} disabled={submitting}
                className="mt-8 w-full py-4 bg-indigo-600 text-white rounded-xl font-black text-base hover:bg-indigo-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Grading...</> : '🎯 Submit Final Assessment'}
            </button>
        </div>
    );
};

// ─── Main Self-Paced View ────────────────────────────────────────────────────
const SelfPacedUnitView = () => {
    const { unitId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const { handleJoinSession } = useLiveClass();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeLesson, setActiveLesson] = useState(null);
    const [completing, setCompleting] = useState(false);
    const [showQuiz, setShowQuiz] = useState(false);
    const [finalQuizData, setFinalQuizData] = useState(null); // { quizId, title }
    const [unitJustCompleted, setUnitJustCompleted] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [enrolling, setEnrolling] = useState(false);
    const [activeVideoUrl, setActiveVideoUrl] = useState(null);
    const contentRef = useRef(null);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        // Also handle the case where content might still be loading/rendering
        const timeoutId = setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
        return () => clearTimeout(timeoutId);
    }, [activeLesson?.id, showQuiz]);

    const handleEnroll = async () => {
        setEnrolling(true);
        try {
            const res = await fetch(`${API}/enrollments/enroll`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ unitId })
            });
            const result = await res.json();
            if (result.requiresPayment) {
                navigate(`/payment/${unitId}`);
            } else {
                await fetchLessons();
            }
        } catch (e) {
            alert('Enrollment failed. Please try again.');
        } finally {
            setEnrolling(false);
        }
    };

    const fetchLessons = useCallback(async () => {
        try {
            const res = await fetch(`${API}/self-paced/unit/${unitId}/lessons`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            if (!res.ok) throw new Error('Failed to load lessons');
            const d = await res.json();
            
            // Payment Access Check: must be paid, confirmed, partial, or unit must be free
            const hasAccess = d.paymentStatus === 'confirmed' || 
                             d.paymentStatus === 'paid' || 
                             d.paymentStatus === 'partial' || 
                             d.unit?.isFree;
            
            if (!hasAccess && d.unitStatus !== 'not_enrolled') {
                alert('Please complete your payment to access the full unit content.');
                navigate(`/unit-details/${unitId}`);
                return;
            }

            setData(d);
            // Default to first unlocked incomplete lesson
            const active = d.lessons.find(l => l.isUnlocked && !l.isCompleted) || d.lessons[d.lessons.length - 1];
            setActiveLesson(active);
        } catch (e) {
            console.error(e);
        } finally { setLoading(false); }
    }, [unitId, navigate]);

    useEffect(() => { fetchLessons(); }, [fetchLessons]);

    // Polling for live class status
    useEffect(() => {
        if (!unitId || !data) return;

        const checkLiveStatus = async () => {
            try {
                const res = await fetch(`${API}/livekit/unit/${unitId}/status`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });
                if (res.ok) {
                    const status = await res.json();
                    setData(prev => {
                        if (!prev) return prev;
                        if (prev.unit.isLive === status.isLive) return prev;
                        return {
                            ...prev,
                            unit: { ...prev.unit, isLive: status.isLive }
                        };
                    });
                }
            } catch (err) {
                console.error('Error polling live status:', err);
            }
        };

        const interval = setInterval(checkLiveStatus, 10000); // Check every 10 seconds
        checkLiveStatus(); // Check immediately on mount/data load

        return () => clearInterval(interval);
    }, [unitId, data === null]);

    const handleCompleteLesson = async () => {
        if (!activeLesson || completing) return;
        setCompleting(true);
        try {
            const res = await fetch(`${API}/self-paced/lesson/${activeLesson.id}/complete`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            const result = await res.json();
            if (!res.ok) { alert(result.message || 'Error completing lesson'); return; }

            if (result.isLastLesson) {
                // Final quiz was auto-generated — show it
                setFinalQuizData(result.finalQuiz);
                setShowQuiz(true);
            } else {
                // Move to next lesson
                await fetchLessons();
            }
        } catch (e) {
            alert('Network error. Please try again.');
        } finally { setCompleting(false); }
    };

    const handleQuizPass = (result) => {
        setUnitJustCompleted(true);
        // We no longer auto-navigate here. 
        // This allows the student to stay and review their answers inline using the new results UI.
    };

    if (loading) return (
        <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
                <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
                <p className="text-gray-500">Loading your lesson...</p>
            </div>
        </div>
    );

    if (!data) return (
        <div className="min-h-screen flex items-center justify-center">
            <div className="text-center">
                <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Could not load lessons for this unit.</p>
                <button onClick={() => navigate('/units')} className="mt-4 px-5 py-2 bg-indigo-600 text-white rounded-lg">
                    Back to Units
                </button>
            </div>
        </div>
    );

    const { unit, lessons, unitStatus, unitProgress, paymentStatus } = data;
    const isAccessible = unit.isFree || (unitStatus !== 'not_enrolled' && (paymentStatus === 'confirmed' || paymentStatus === 'partial'));
    const allDone = lessons.every(l => l.isCompleted);
    const unitCompleted = unitStatus === 'completed' || unitStatus === 'passed';
    const isLastLesson = activeLesson && lessons[lessons.length - 1]?.id === activeLesson.id;

    if (!isAccessible) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col overflow-x-hidden">
                <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 sticky top-0 z-40 shadow-sm">
                    <button
                        onClick={() => navigate('/units', { state: location.state })}
                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5 text-gray-600" />
                    </button>
                    <h1 className="font-bold text-gray-900 truncate text-sm sm:text-base">Enrollment Required</h1>
                </div>

                <div className="flex-1 flex items-center justify-center p-6">
                    <div className="max-w-md w-full bg-white rounded-3xl border border-gray-200 shadow-xl overflow-hidden flex flex-col">
                        <CardThumbnail name={unit.name} imageUrl={getImageUrl(unit.imageUrl)} className="h-48" />
                        <div className="bg-indigo-600 p-8 text-center relative">
                            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
                                {unitStatus === 'not_enrolled' ? (
                                    <ShieldCheck className="w-10 h-10 text-white" />
                                ) : (
                                    <CreditCard className="w-10 h-10 text-white" />
                                )}
                            </div>
                            <h2 className="text-2xl font-black text-white mb-2">{unit.name}</h2>
                            <p className="text-indigo-100 text-sm">
                                {unitStatus === 'not_enrolled'
                                    ? "Enroll today to unlock the full curriculum and start your learning journey."
                                    : "Completion of payment is required to access these lessons."}
                            </p>
                        </div>

                        <div className="p-8">
                            <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-100">
                                <span className="text-gray-500 font-medium">Registration Fee</span>
                                <span className="text-2xl font-black text-gray-900">
                                    {unit.cost ? `${unit.currency} ${unit.cost.toLocaleString()}` : "TBD"}
                                </span>
                            </div>

                            <button
                                onClick={unitStatus === 'not_enrolled' ? handleEnroll : () => navigate(`/payment/${unitId}`)}
                                disabled={enrolling}
                                className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-black text-lg hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 flex items-center justify-center gap-3 disabled:opacity-70"
                            >
                                {enrolling ? <Loader2 className="w-6 h-6 animate-spin" /> : (
                                    unitStatus === 'not_enrolled' ? "Enroll Now" : "Pay Now"
                                )}
                                <ChevronRight className="w-6 h-6" />
                            </button>

                            <button
                                onClick={() => navigate('/units', { state: location.state })}
                                className="w-full mt-4 py-2 text-gray-400 font-bold hover:text-gray-600 transition-colors text-sm"
                            >
                                Back to Course Catalog
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col overflow-x-hidden">
            {/* Top nav - Fixed for guaranteed stickiness across all mobile browsers */}
            <div className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 px-4 py-3 sm:py-4 flex items-center gap-3 shadow-sm">
                <button
                    onClick={() => navigate('/units', { state: location.state })}
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                    <ArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
                <div className="flex-1 min-w-0">
                    <h1 className="font-bold text-gray-900 truncate text-sm sm:text-base">{unit.name}</h1>
                    <div className="flex items-center gap-2 mt-0.5">
                        <div className="flex-1 bg-gray-200 rounded-full h-1.5 max-w-xs">
                            <div className="bg-indigo-600 h-1.5 rounded-full transition-all duration-700"
                                style={{ width: `${unitProgress || 0}%` }} />
                        </div>
                        <span className="text-xs text-gray-500 font-medium">{unitProgress || 0}%</span>
                    </div>
                </div>

                <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="sm:hidden p-2 rounded-lg hover:bg-gray-100 text-indigo-600 flex items-center gap-2"
                >
                    <Menu className="w-5 h-5" />
                    <span className="text-xs font-bold uppercase tracking-wider">Lessons</span>
                </button>
                <BookOpen className="text-indigo-400 hidden sm:block w-5 h-5" />
            </div>

            {/* Spacer to account for fixed header height */}
            <div className="h-[68px] sm:h-[77px] flex-shrink-0" />

            <div className="flex flex-col sm:flex-row flex-1 relative">
                {/* ── MOBILE OVERLAY ── */}
                {sidebarOpen && (
                    <div
                        className="fixed inset-0 bg-black/30 z-40 sm:hidden transition-opacity"
                        onClick={() => setSidebarOpen(false)}
                    />
                )}

                {/* ── LEFT SIDEBAR: Lesson Roadmap ── */}
                <div className={`
                    fixed inset-y-0 left-0 sm:relative z-[60] sm:z-auto
                    w-72 sm:w-64 bg-white border-r border-gray-200 flex-shrink-0 h-full sm:min-h-screen flex flex-col
                    transform transition-transform duration-300 ease-in-out
                    ${sidebarOpen ? 'translate-x-0' : '-translate-x-full sm:translate-x-0'}
                `}>
                    <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/80">
                        <h2 className="text-xs font-black uppercase tracking-widest text-gray-500">Lesson Roadmap</h2>
                        <button onClick={() => setSidebarOpen(false)} className="sm:hidden p-1 text-gray-400 hover:text-gray-900 rounded-full hover:bg-gray-200">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="flex-1 p-3 space-y-1 overflow-y-auto custom-scrollbar">
                        {lessons.map((lesson, index) => {
                            const isActive = activeLesson?.id === lesson.id && !showQuiz;
                            return (
                                <button
                                    key={lesson.id}
                                    onClick={() => {
                                        if (lesson.isUnlocked) {
                                            setActiveLesson(lesson);
                                            setShowQuiz(false);
                                            setSidebarOpen(false); // Auto-close on mobile
                                        }
                                    }}
                                    disabled={!lesson.isUnlocked}
                                    className={`w-full text-left p-3 rounded-xl transition-all flex items-start gap-3 group ${isActive
                                        ? 'bg-indigo-600 text-white shadow-md'
                                        : lesson.isCompleted
                                            ? 'bg-green-50 text-green-800 hover:bg-green-100'
                                            : lesson.isUnlocked
                                                ? 'bg-white hover:bg-indigo-50 text-gray-700 border border-gray-100'
                                                : 'opacity-40 cursor-not-allowed bg-gray-50 text-gray-400'
                                        }`}
                                >
                                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-black mt-0.5 ${isActive ? 'bg-white text-indigo-600'
                                        : lesson.isCompleted ? 'bg-green-600 text-white'
                                            : lesson.isUnlocked ? 'bg-indigo-100 text-indigo-600'
                                                : 'bg-gray-200 text-gray-400'
                                        }`}>
                                        {lesson.isCompleted ? <CheckCircle className="w-4 h-4" /> : !lesson.isUnlocked ? <Lock className="w-3 h-3" /> : index + 1}
                                    </div>
                                    <span className={`text-xs font-semibold leading-snug ${isActive ? 'text-white' : ''}`}>{lesson.title}</span>
                                </button>
                            );
                        })}

                        {/* Final quiz step */}
                        {allDone && (
                            <button
                                onClick={() => { setShowQuiz(true); setSidebarOpen(false); }}
                                className={`w-full text-left p-3 rounded-xl transition-all flex items-start gap-3 ${showQuiz ? 'bg-yellow-500 text-white shadow-md' :
                                    unitCompleted ? 'bg-green-50 text-green-800 border border-green-200' :
                                        'bg-    yellow-50 text-yellow-800 hover:bg-yellow-100 border border-yellow-200'
                                    }`}
                            >
                                <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${unitCompleted ? 'bg-green-500 text-white' : 'bg-yellow-400 text-white'
                                    }`}>
                                    {unitCompleted ? <CheckCircle className="w-3.5 h-3.5" /> : <Trophy className="w-3.5 h-3.5" />}
                                </div>
                                <span className="text-xs font-semibold">
                                    {unitCompleted ? 'Assessment Completed' : 'Final Assessment'}
                                </span>
                            </button>
                        )}
                    </div>
                </div>

                {/* ── MAIN CONTENT ── */}
                <div
                    ref={contentRef}
                    className="flex-1 w-full min-w-0"
                >
                    {showQuiz ? (
                        <div className="max-w-6xl mx-auto px-2 sm:px-8 py-8">
                            <FinalQuiz unitId={unitId} onPass={handleQuizPass} />
                        </div>
                    ) : activeLesson ? (
                        <>
                            {/* Academic Resources (Sticky - offset by the top nav height) */}
                            <div className="sticky top-[68px] sm:top-[73px] z-20 bg-gray-50/95 backdrop-blur-sm border-b border-gray-200 shadow-sm w-full transition-all">
                                <div className="max-w-6xl mx-auto px-2 sm:px-8 pt-2 pb-3">
                                    <h3 className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2.5 ml-1">Academic Resources</h3>
                                    <div className="flex overflow-x-auto pb-1 gap-3 snap-x hide-scrollbar">
                                        <button
                                            onClick={() => unit?.isLive ? handleJoinSession(unitId) : null}
                                            disabled={!unit?.isLive}
                                            className={`snap-start flex-shrink-0 w-64 p-3 bg-white border border-gray-100 rounded-xl transition-all flex items-start gap-3 group text-left relative overflow-hidden ${unit?.isLive ? 'hover:shadow-md hover:border-red-200 cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}
                                        >
                                            <div className="p-2 bg-red-50 rounded-lg text-red-600 group-hover:scale-110 transition-transform relative z-10">
                                                <Video className="w-5 h-5" />
                                            </div>
                                            <div className="relative z-10">
                                                <div className="font-bold text-gray-900 text-sm">Virtual Class</div>
                                                <div className="text-[10px] text-gray-500 mt-0.5">{unit?.isLive ? 'Session is live' : 'No active session'}</div>
                                            </div>
                                            {unit?.isLive && (
                                                <>
                                                    <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-red-500 animate-pulse z-10" />
                                                    <div className="absolute inset-0 bg-red-50/20 z-0" />
                                                </>
                                            )}
                                        </button>

                                        <button className="snap-start flex-shrink-0 w-64 p-3 bg-gradient-to-br from-indigo-50/50 to-blue-50/50 border border-blue-100 rounded-xl hover:shadow-md hover:border-blue-200 transition-all flex items-start gap-3 group text-left">
                                            <div className="p-2 bg-white rounded-lg shadow-sm text-blue-600 group-hover:scale-110 transition-transform">
                                                <UploadCloud className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <div className="font-bold text-gray-900 text-sm">Unit Materials</div>
                                                <div className="text-[10px] text-gray-500 mt-0.5">Share notes & resources</div>
                                            </div>
                                        </button>

                                        <button className="snap-start flex-shrink-0 w-64 p-3 bg-white border border-gray-100 rounded-xl hover:shadow-md hover:border-green-200 transition-all flex items-start gap-3 group text-left">
                                            <div className="p-2 bg-green-50 rounded-lg text-green-600 group-hover:scale-110 transition-transform">
                                                <FileText className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <div className="font-bold text-gray-900 text-sm">Assignments</div>
                                                <div className="text-[10px] text-gray-500 mt-0.5">Create tasks & quizzes</div>
                                            </div>
                                        </button>

                                        <button className="snap-start flex-shrink-0 w-64 p-3 bg-white border border-gray-100 rounded-xl hover:shadow-md hover:border-purple-200 transition-all flex items-start gap-3 group text-left">
                                            <div className="p-2 bg-purple-50 rounded-lg text-purple-600 group-hover:scale-110 transition-transform">
                                                <MessageSquare className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <div className="font-bold text-gray-900 text-sm">Discussion</div>
                                                <div className="text-[10px] text-gray-500 mt-0.5">Student interactions</div>
                                            </div>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="max-w-6xl mx-auto px-2 sm:px-8 pt-6 pb-6">
                                {/* Lesson Header */}
                                <div className="mb-6">
                                    <div className="flex items-center gap-2 text-xs text-indigo-500 font-bold uppercase tracking-widest mb-2">
                                        <Play className="w-3 h-3" />
                                        Lesson {activeLesson.order}
                                    </div>
                                    <h2 className="text-2xl font-black text-gray-900">{activeLesson.title}</h2>
                                </div>

                                {/* Notes Content */}
                                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8 mb-8">
                                    {activeLesson.isCompleted && (
                                        <div className="mb-4 flex items-center gap-2 text-green-600 text-sm font-semibold bg-green-50 rounded-lg px-3 py-2">
                                            <CheckCircle className="w-4 h-4" /> Lesson completed
                                        </div>
                                    )}
                                    <MarkdownRenderer content={activeLesson.description} onVideoClick={setActiveVideoUrl} />
                                </div>

                                {/* Action Footer */}
                                {!activeLesson.isCompleted ? (
                                    <button
                                        onClick={handleCompleteLesson}
                                        disabled={completing}
                                        className="w-full py-4 bg-indigo-600 text-white rounded-xl font-black text-base hover:bg-indigo-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2 shadow-lg shadow-indigo-200"
                                    >
                                        {completing ? (
                                            <><Loader2 className="w-5 h-5 animate-spin" /> {isLastLesson ? 'Saving progress, fetching your quiz...' : 'Saving progress...'}</>
                                        ) : allDone ? (
                                            <><Trophy className="w-5 h-5" /> Take Final Assessment</>
                                        ) : (
                                            <>Mark Complete & Continue <ChevronRight className="w-5 h-5" /></>
                                        )}
                                    </button>
                                ) : (
                                    <div className="text-center">
                                        {unitCompleted ? (
                                            <button
                                                onClick={() => {
                                                    // Navigate to the assignments page deep-linked to the QUIZZES tab for this specific unit
                                                    // Use the 'tab' and 'unitId' query params supported by Assignments.js
                                                    navigate(`/assignments?tab=quizzes&unitId=${unitId}`);
                                                }}
                                                className="px-8 py-4 bg-green-600 text-white rounded-xl font-black text-base hover:bg-green-700 transition-colors shadow-lg shadow-green-200 flex items-center gap-2 mx-auto"
                                            >
                                                <CheckCircle className="w-5 h-5" /> Assessment Passed
                                            </button>
                                        ) : allDone ? (
                                            <button
                                                onClick={() => setShowQuiz(true)}
                                                className="px-8 py-4 bg-yellow-500 text-white rounded-xl font-black text-base hover:bg-yellow-600 transition-colors shadow-lg shadow-yellow-200 flex items-center gap-2 mx-auto"
                                            >
                                                <Trophy className="w-5 h-5" /> Take Final Assessment
                                            </button>
                                        ) : (
                                            <p className="text-gray-400 text-sm">Select the next lesson from the sidebar.</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="max-w-6xl mx-auto px-2 sm:px-8 py-8 w-full">
                            <div className="text-center py-20">
                                <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-500">No lessons available for this unit yet.</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Global Video Player Modal */}
            <VideoModal url={activeVideoUrl} onClose={() => setActiveVideoUrl(null)} />
        </div>
    );
};

export default SelfPacedUnitView;
